import { ValidationService } from '@/common/services/validation/validation.service';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import _ from 'lodash';
import { isValid } from '@/common/utils/validation/common.utils';
import { DoReservationUseCase } from '@/core/domain/reservation/usecases/doReservation.usecase';
import { catchError, first, of, switchMap, throwError } from 'rxjs';
import {
    Payment,
    PaymentResponse,
} from '@/core/domain/reservation/models/payment-response.model';
import { CurrentReservationService } from '@/common/services/reservation/current';
import { Store } from '@ngrx/store';
import { selectReservation } from '@/core/reservation/store/reservation.selector';
import { ActivatedRoute, Router } from '@angular/router';
import { AddressService } from '@/common/services/address/address.service';
import { GetCountriesUseCase } from '@/core/domain/address/usecases/getCountries.usecase';
import { GetStatesUseCase } from '@/core/domain/address/usecases/getSates.usecase';
import { GetCitiesUseCase } from '@/core/domain/address/usecases/getCities.usecase';
import { GetNeighborhoodsUseCase } from '@/core/domain/address/usecases/getNeighborhoods.usecase';
import { BreakpointService } from '@/common/services/breakpoint/breakpoint.service';
import { AlertService } from '@/common/services/alert/alert.service';
import { environment } from '@/env/environment';
import { GtmBeginCheckoutUseCase } from '@/core/domain/gtm/usecases/gtmBeginCheckout.usecase';
import { UrlService } from '@/common/services/url/url.service';
import { ConfigurationService } from '@/common/services/configuration/configuration.service';
import { PersonalInformation } from '@/core/domain/reservation/models/personal-information.model';
import { SummaryService } from '@/common/services/summary/summary.service';
import { ProgressBarComponent } from '@/ui/shared/components/progress-bar/progress-bar.component';
import { crpInternational } from '@/core/domain/car-rental-policy/models/crp-body';
import { CartStep2InsuranceInterface } from '@/core/domain/cart-step2/models/cart-step2-insurance.model';
import { sendCartStep2InsuranceUseCase } from '@/core/domain/cart-step2/usecases/sendCartStep2Insurance.usecase';
import { sendCartStep2ProductUseCase } from '@/core/domain/cart-step2/usecases/sendCartStep2Product.usecase';
import { updateReservation } from '@/core/reservation/store/reservation.actions';

@Component({
    selector: 'fe-step-4',
    templateUrl: './step-4.component.html',
    styleUrls: ['./step-4.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class Step4Component implements OnInit, AfterViewInit {
    public formPersonalInfo!: FormGroup;

    public formBBVA!: Payment | undefined;

    @ViewChild('formBBVAelement') formBBVAelement!: ElementRef;

    public checkBoxs = {
        checkPromotions: true,
        checkTerms: true,
    };

    public urlActionBBVA!: string | undefined;

    public loadingBtnReservate = false;

    public loadingCountries = false;
    public loadingStates = false;
    public loadingCities = false;
    public loadingNeighborhoods = false;

    public crpInternational?: crpInternational;

    public successUrl!: string;
    public errorUrl!: string;

    @ViewChild('progressBarDesktop') progressBarDesktop!: ProgressBarComponent;

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly validation: ValidationService,
        private readonly doReservationUseCase: DoReservationUseCase,
        private readonly store: Store,
        private readonly router: Router,
        public reservation: CurrentReservationService,
        public addressService: AddressService,
        private readonly getCountriesUseCase: GetCountriesUseCase,
        private readonly getStatesUseCase: GetStatesUseCase,
        private readonly getCitiesUseCase: GetCitiesUseCase,
        private readonly getNeighborhoodsUseCase: GetNeighborhoodsUseCase,
        public breakpoint: BreakpointService,
        private readonly route: ActivatedRoute,
        private readonly alert: AlertService,
        private readonly gtmBeginCheckout: GtmBeginCheckoutUseCase,
        private readonly url: UrlService,
        public readonly configuration: ConfigurationService,
        public readonly summaryService: SummaryService,
        private readonly cdr: ChangeDetectorRef,
        private readonly sendCartStep2InsuranceUseCase: sendCartStep2InsuranceUseCase,
        private readonly sendCartStep2ProductUseCase: sendCartStep2ProductUseCase,
    ) {
        this.initFormPersonalInfo();
    }

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }

    public hasCart = false;

    public ngOnInit(): void {
        this.store
            .select(selectReservation)
            .pipe(first())
            .subscribe((reservation) => {
                this.hasCart = isValid(_.get(reservation, 'uuid'));

                if (!this.hasCart) {
                    this.router.navigate([this.url.format('')]);
                    return;
                }

                this.checkOnErrorPrepaid();
                this.configuration.reloadFeatureFlags();
                this.gtmBeginCheckout.execute().subscribe();
            });
    }

    private checkOnErrorPrepaid(): void {
        if (
            this.route.snapshot.queryParamMap.get('paymentStatus') !== 'FAILED'
        ) {
            return;
        }
        this.alert.open();
    }

    public onClicReservate(): void {
        this.formPersonalInfo.markAllAsTouched();
        if (
            !this.formPersonalInfo.valid ||
            this.checkBoxs.checkTerms !== true
        ) {
            return;
        }
        this.sendReservation();
    }

    private sendReservation(): void {
        this.loadingBtnReservate = true;
        this.doReservationUseCase
            .execute(this.buildReservationRequest())
            .pipe(
                catchError((error) => {
                    this.loadingBtnReservate = false;
                    this.alert.openDefault(error);
                    return throwError(() => error);
                }),
                switchMap((doReservationResponse: PaymentResponse) => {
                    this.loadingBtnReservate = false;
                    this.onDoReservationResponse(doReservationResponse);
                    return of(doReservationResponse);
                }),
            )
            .subscribe();
    }

    private buildReservationRequest(): PersonalInformation {
        return {
            buildingNumber: this.formPersonalInfo.get('outdoorNumber')?.value,
            checkPromotions: this.checkBoxs.checkPromotions,
            checkTerms: this.checkBoxs.checkTerms,
            email: this.formPersonalInfo.get('email')?.value,
            lastName: this.formPersonalInfo.get('lastName')?.value,
            name: this.formPersonalInfo.get('names')?.value,
            phone: [
                this.formPersonalInfo.get('lada')?.value,
                this.formPersonalInfo.get('phone')?.value,
            ]
                .filter(Boolean)
                .join(' '),
            travelReason: this.formPersonalInfo.get('typeTrip')?.value,
            age: this.reservation.current.age as any,
            successUrl: this.successUrl,
            errorUrl: this.errorUrl,
            sessionId: this.reservation.current.sessionId as string,
            isInternational: this.reservation.current.origin === 'INT',
        };
    }

    private onDoReservationResponse(doReservationResponse: any): void {
        if (_.get(doReservationResponse, 'data.url', false) === false) {
            this.router.navigate(
                [this.url.format('reservations/reservation-complete')],
                {
                    queryParams: {
                        reservationCode: _.get(
                            doReservationResponse,
                            'data.reservationCode',
                        ),
                        lastName: _.get(doReservationResponse, 'data.lastName'),
                    },
                },
            );
        } else {
            this.urlActionBBVA = doReservationResponse.data?.url;
            this.formBBVA = doReservationResponse.data?.payment;
            setTimeout(() => {
                this.formBBVAelement.nativeElement.submit();
            });
        }
    }

    private initFormPersonalInfo(): void {
        this.successUrl = [
            environment.baseUrl,
            this.url.format('reservations/reservation-complete'),
        ].join('');
        this.errorUrl = [
            environment.baseUrl,
            this.url.format('reservations/reservation-summary'),
        ].join('');
        this.store
            .select(selectReservation)
            .pipe(
                first(),
                switchMap((reservation) => {
                    this.onGetReservation(
                        _.get(reservation, 'location.location'),
                    );

                    if (reservation.origin !== 'NAT') {
                        this.crpInternational = {
                            stationId: reservation.location.id,
                            pickup: reservation.startDate,
                            return: reservation.endDate,
                        };
                    } else if (!_.isEmpty(reservation.insurances)) {
                        const insuranceData: CartStep2InsuranceInterface = {
                            sessionId: reservation.uuid,
                            insuranceId: reservation.insurances[0].id,
                            isPrePaid: false,
                        };

                        return this.sendCartStep2InsuranceUseCase
                            .execute(insuranceData)
                            .pipe(
                                switchMap((response) => {
                                    this.store.dispatch(
                                        updateReservation({
                                            value: {
                                                total: response.data?.amount,
                                            },
                                        }),
                                    );
                                    return of(true);
                                }),
                                catchError((error) => {
                                    this.alert.openDefault(error);
                                    return of(null);
                                }),
                            );
                    }

                    return of(null);
                }),
            )
            .subscribe();
    }

    private onGetReservation(location: string): void {
        let formGroup = {
            names: ['', Validators.compose([Validators.required])],
            lastName: ['', Validators.compose([Validators.required])],
            email: [
                '',
                Validators.compose([
                    Validators.required,
                    this.validation.validEmail,
                ]),
            ],
        };

        if (location === 'NAT') {
            formGroup = _.merge({}, formGroup, {
                lada: ['+52'],
                phone: [
                    '',
                    Validators.compose([
                        Validators.required,
                        this.validation.validPhone,
                    ]),
                ],
                typeTrip: ['Placer'],
            });
        }

        this.formPersonalInfo = this.formBuilder.group(formGroup);
    }

    public onlyLetters(event: KeyboardEvent) {
        const text = /^[a-zA-Z\s]*$/;
        if (!text.test(event.key)) {
            event.preventDefault();
        }
    }
}
